import { X, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DOCUMENT_TYPE_LABELS, mockClients } from "../mockData";
import type { MockDocument } from "../types";

interface DocumentPreviewPanelProps {
  document: MockDocument | null;
  onClose: () => void;
}

export function DocumentPreviewPanel({ document, onClose }: DocumentPreviewPanelProps) {
  if (!document) return null;

  const client = mockClients.find((c) => c.id === document.clientId);

  return (
    <div className="w-96 border-l bg-white flex flex-col h-full overflow-y-auto">
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold text-sm truncate pr-2">Document Preview</h3>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="p-4 flex flex-col gap-4">
        {/* Preview placeholder */}
        <div className="bg-neutral-100 rounded-lg border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center py-16">
          <FileText className="w-12 h-12 text-neutral-400 mb-2" />
          <span className="text-sm text-neutral-500">Preview not available</span>
          <span className="text-xs text-neutral-400">in demo mode</span>
        </div>

        {/* Metadata */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div>
              <span className="text-xs text-neutral-500">Filename</span>
              <p className="text-sm font-medium break-all">
                {document.storedFilename || document.originalFilename}
              </p>
              {document.storedFilename && (
                <p className="text-xs text-neutral-400">{document.originalFilename}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-xs text-neutral-500">Type</span>
                <p className="text-sm">{DOCUMENT_TYPE_LABELS[document.documentType]}</p>
              </div>
              <div>
                <span className="text-xs text-neutral-500">Status</span>
                <div className="mt-0.5">
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      document.status === "processed"
                        ? "bg-green-100 text-green-700 border-green-200"
                        : document.status === "processing"
                          ? "bg-amber-100 text-amber-700 border-amber-200"
                          : "bg-red-100 text-red-700 border-red-200"
                    }`}
                  >
                    {document.status}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-xs text-neutral-500">Document Date</span>
                <p className="text-sm">{document.documentDate}</p>
              </div>
              <div>
                <span className="text-xs text-neutral-500">Upload Date</span>
                <p className="text-sm">{document.uploadDate}</p>
              </div>
            </div>

            {document.counterpartyName && (
              <div>
                <span className="text-xs text-neutral-500">Counterparty</span>
                <p className="text-sm">{document.counterpartyName}</p>
                {document.counterpartyEik && (
                  <p className="text-xs text-neutral-400">EIK: {document.counterpartyEik}</p>
                )}
              </div>
            )}

            {document.amount > 0 && (
              <div>
                <span className="text-xs text-neutral-500">Amount</span>
                <p className="text-sm font-medium">
                  {document.amount.toLocaleString("bg-BG", { minimumFractionDigits: 2 })}{" "}
                  {document.currency}
                </p>
              </div>
            )}

            {client && (
              <div>
                <span className="text-xs text-neutral-500">Client</span>
                <p className="text-sm">{client.name}</p>
                <p className="text-xs text-neutral-400">EIK: {client.eik}</p>
              </div>
            )}

            {document.description && (
              <div>
                <span className="text-xs text-neutral-500">Description</span>
                <p className="text-sm">{document.description}</p>
              </div>
            )}

            {document.aiConfidence > 0 && (
              <div>
                <span className="text-xs text-neutral-500">AI Confidence</span>
                <p className="text-sm">{(document.aiConfidence * 100).toFixed(0)}%</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
